import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ROUTES } from "@/shared/model/routes";
import { websocketClient, TicketWSMessage } from "@/shared/websocket";

export function useTicketsSubscription() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = websocketClient.subscribeToTickets(
      (message: TicketWSMessage) => {
        console.log("[WS] Tickets:", message);

        switch (message.action) {
          case "created": {
            const { ticket } = message;
            toast.info(
              t("tickets.ticketCreated", {
                defaultValue: "Новый тикет создан",
              }),
              {
                description: ticket.subject,
                duration: 5000,
                action: {
                  label: (
                    <ExternalLink className="size-3.5 text-muted-foreground" />
                  ),
                  onClick: () => navigate(`${ROUTES.TICKETS}/${ticket.id}`),
                },
                className: "group",
                actionButtonStyle: {
                  backgroundColor: "transparent",
                  position: "absolute",
                  top: "0.5rem",
                  right: "0.5rem",
                  padding: "0.25rem",
                },
              },
            );
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/v1/tickets"],
            });
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/v1/tickets/unread_count"],
            });
            break;
          }

          case "new_message": {
            const { message: msg, ticket_id } = message;

            // Only show toast for messages from admin
            if (msg.sender.type === "admin") {
              const content =
                msg.content.length > 100
                  ? msg.content.slice(0, 100) + "..."
                  : msg.content;

              toast.success(
                t("tickets.newMessage", {
                  defaultValue: "Новое сообщение от поддержки",
                }),
                {
                  description: content,
                  duration: 7000,
                  action: {
                    label: (
                      <ExternalLink className="size-3.5 text-muted-foreground" />
                    ),
                    onClick: () => navigate(`${ROUTES.TICKETS}/${ticket_id}`),
                  },
                  className: "group",
                  actionButtonStyle: {
                    backgroundColor: "transparent",
                    position: "absolute",
                    top: "0.5rem",
                    right: "0.5rem",
                    padding: "0.25rem",
                  },
                },
              );
            }

            // Invalidate queries
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/v1/tickets"],
            });
            queryClient.invalidateQueries({
              queryKey: [
                "get",
                "/api/v1/tickets/{ticket_id}/messages",
                { params: { path: { ticket_id } } },
              ],
            });
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/v1/tickets/unread_count"],
            });
            break;
          }

          case "message_read": {
            const { ticket_id } = message;
            // Admin read user's message - invalidate to update read status
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/v1/tickets"],
            });
            queryClient.invalidateQueries({
              queryKey: [
                "get",
                "/api/v1/tickets/{ticket_id}/messages",
                { params: { path: { ticket_id } } },
              ],
            });
            queryClient.invalidateQueries({
              queryKey: ["get", "/api/v1/tickets/unread_count"],
            });
            break;
          }
        }
      },
    );

    return () => unsubscribe();
  }, [queryClient, t, navigate]);
}
